// Materialize's DatePicker doesn't localize by default (its i18n is English),
// so it needs an explicit Spanish month/weekday set to match the rest of the form.
const EVENT_DATEPICKER_I18N = {
    cancel: 'Cancelar',
    clear: 'Limpiar',
    done: 'Aceptar',
    previousMonth: '‹',
    nextMonth: '›',
    months: [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ],
    monthsShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'],
    weekdays: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    weekdaysShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    weekdaysAbbrev: ['D', 'L', 'M', 'M', 'J', 'V', 'S']
};

const PAYMENT_IMAGE_MAX_DIMENSION = 1080; // Longest side, in px
const PAYMENT_IMAGE_QUALITY = 0.8;

const formatDateForFilename = (date) => {
    const pad = (value) => String(value).padStart(2, '0');
    return date.getFullYear().toString() +
        pad(date.getMonth() + 1) +
        pad(date.getDate()) +
        pad(date.getHours()) +
        pad(date.getMinutes()) +
        pad(date.getSeconds());
}

const getPaymentImageName = () => `payment-${formatDateForFilename(new Date())}.webp`

const convertImageToWebp = (file) => {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const scale = Math.min(1, PAYMENT_IMAGE_MAX_DIMENSION / Math.max(image.width, image.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);

            const context = canvas.getContext('2d');
            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('No se pudo convertir la imagen a webp.'));
                    return;
                }
                resolve(new File([blob], getPaymentImageName(), { type: 'image/webp' }));
            }, 'image/webp', PAYMENT_IMAGE_QUALITY);
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('No se pudo cargar la imagen seleccionada.'));
        };

        image.src = objectUrl;
    });
}

const getStep2MandatoryFields = (selectedTypeId) => {
    return getMandatoryFields(['organizerName', 'phone'], selectedTypeId);
}

const getStep3MandatoryFields = (selectedPaymentMethodId) => {
    const fields = ['paymentMethodId'];
    if (selectedPaymentMethodId) {
        if ( Number(selectedPaymentMethodId) === 7) {
            fields.push('paymentCashAmount');
        } else {
            fields.push('paymentScreenshot');
        }
    }
    return fields;
}

const getEditAllMandatoryFields = (selectedTypeId) => {
    return [
        ...getEditOrganizerMantadoryFields(),
        ...getEditEventMandatoryFields(selectedTypeId)
    ];
};


const getEditOrganizerMantadoryFields = () => {
    return ['organizerName', 'phone'];
}

const getEditEventMandatoryFields = (selectedTypeId) => {
    return getMandatoryFields([], selectedTypeId);
}

const COMMON_EVENT_FIELDS = ['typeCatLv1', 'eventDate', 'eventTime', 'address'];

const getMandatoryFields = (extraBaseFields = [], selectedTypeId) => {
    const typeId = Number(selectedTypeId);
    const fields = [...COMMON_EVENT_FIELDS, ...extraBaseFields];

    if (typeId === 1 || typeId === 2) {
        fields.push('celebrantName');
    }
    if (typeId === 1) {
        fields.push('birthNumber');
    }
    if (typeId === 4) {
        fields.push('couplePerson1Name', 'couplePerson2Name');
    }

    return fields;
};


// Parses the value M.Timepicker writes into its input in twelveHour mode
// ("hh:mm AM" / "hh:mm PM") into 24-hour { hours, minutes }.
// Returns null when `value` isn't a recognizable time (e.g. empty, or a
// hand-typed value), so callers can report it instead of crashing.
// Accepts Materialize's 12-hour output ('06:00 PM') and 24-hour ('18:00').
const parseEventTime = (value) => {
    const match = (value || '').match(/(\d{1,2}):(\d{2})\s*([AP]\.?\s*M\.?)?/i);
    if (!match) return null;
    const [, hh, mm, amOrPm] = match;
    const hours = Number(hh);
    const minutes = Number(mm);
    if (minutes > 59) return null;
    if (!amOrPm) return hours > 23 ? null : { hours, minutes };
    if (hours < 1 || hours > 12) return null;
    const isPm = amOrPm.toUpperCase().startsWith('P');
    return { hours: (hours % 12) + (isPm ? 12 : 0), minutes };
};

// The picked date and time are wall-clock values in the browser's timezone:
// whoever books from outside Peru enters the time already converted to it.

// `date` is the DatePicker's Date (only its day is used) and `time` comes from
// parseEventTime. Returns the UTC ISO string sent as `eventDateTime`.
const toEventDateTimeUTC = (date, time) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.hours, time.minutes).toISOString();

// UTC ISO string -> local { date: 'YYYY-MM-DD', time: 'HH:mm' }, or null if invalid.
const toLocalDateTimeParts = (isoString) => {
    const date = new Date(isoString ?? '');
    if (!isoString || isNaN(date)) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return {
        date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
        time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
    };
};

export {
    convertImageToWebp,
    toEventDateTimeUTC,
    toLocalDateTimeParts,
    getPaymentImageName,
    getStep2MandatoryFields,
    getStep3MandatoryFields,
    getEditOrganizerMantadoryFields,
    getEditEventMandatoryFields,
    getEditAllMandatoryFields,
    parseEventTime,
    EVENT_DATEPICKER_I18N
}
